import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Button, Chip, InputBase, Skeleton, Stack } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useNavigate } from "react-router-dom";

import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import SearchBookModal from "../components/SearchBookModal";
import {
  Book,
  BookHome,
  categoryIds,
  categoryLabels,
  DEFAULT_BOOK_IMAGE,
  emptyBookHome,
  getSafeExternalUrl,
  normalizeBook,
  normalizeBookHome,
} from "./book-home";

interface Props {
  userState: userState;
}

type LoadStatus = "loading" | "ready" | "error";

const mainApi = new HttpMainApi();

function getRandomIndex(length: number) {
  return length ? Math.floor(Math.random() * length) : 0;
}

function BookCover({ book, className }: { book: Book; className?: string }) {
  return (
    <img
      className={className}
      src={book.image || DEFAULT_BOOK_IMAGE}
      alt={`${book.title} 표지`}
      onError={(event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src = DEFAULT_BOOK_IMAGE;
      }}
    />
  );
}

function BorrowedBadge({ book }: { book: Book }) {
  return book.isBorrowed ? <span className="hp-book-borrowed-badge">대여중</span> : null;
}

const MobileBookTemplate = (_props: Props) => {
  const navigate = useNavigate();
  const isComposing = useRef(false);
  const [searchText, setSearchText] = useState("");
  const [searchInputError, setSearchInputError] = useState("");
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchStatus, setSearchStatus] = useState<LoadStatus>("ready");
  const [searchError, setSearchError] = useState("");
  const [searchResults, setSearchResults] = useState<Book[]>([]);
  const [bookStatus, setBookStatus] = useState<LoadStatus>("loading");
  const [bookError, setBookError] = useState("");
  const [bookHome, setBookHome] = useState<BookHome>(emptyBookHome);
  const [recommendationIndex, setRecommendationIndex] = useState(0);

  const recommendation = useMemo(() => {
    const candidates = bookHome.recommended.length ? bookHome.recommended : bookHome.newBooks;
    if (!candidates.length) return null;
    return candidates[recommendationIndex % candidates.length];
  }, [bookHome.newBooks, bookHome.recommended, recommendationIndex]);
  const recommendationCandidates = bookHome.recommended.length ? bookHome.recommended : bookHome.newBooks;

  const recommendationDate = useMemo(
    () =>
      new Intl.DateTimeFormat("ko-KR", {
        month: "long",
        day: "numeric",
        weekday: "short",
      }).format(new Date()),
    [],
  );

  const loadBookHome = async () => {
    setBookStatus("loading");
    setBookError("");
    setRecommendationIndex(0);

    try {
      const response = await mainApi.get_book_home({});
      if (response.code !== "200") throw new Error("도서 목록을 불러오지 못했습니다.");
      const home = normalizeBookHome(response.response?.result);
      const candidates = home.recommended.length ? home.recommended : home.newBooks;
      setRecommendationIndex(getRandomIndex(candidates.length));
      setBookHome(home);
      setBookStatus("ready");
    } catch (error) {
      setBookHome(emptyBookHome());
      setBookStatus("error");
      setBookError(error instanceof Error ? error.message : "도서 목록을 불러오는 중 문제가 발생했습니다.");
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    void loadBookHome();
  }, []);

  const handleSearch = async () => {
    const titleName = searchText.trim();
    if (!titleName) {
      setSearchInputError("검색어를 입력해 주세요.");
      return;
    }

    setSearchInputError("");
    setSearchModalOpen(true);
    setSearchStatus("loading");
    setSearchError("");
    setSearchResults([]);

    try {
      const response = await mainApi.get_book_list({ pk: "book", titleName });
      if (response.code === "200") {
        const books = Array.isArray(response.response?.result)
          ? response.response.result.map(normalizeBook).filter((book: Book | null): book is Book => book !== null)
          : [];
        setSearchResults(books);
        setSearchStatus("ready");
        return;
      }

      if (response.code === "400") {
        setSearchStatus("ready");
        return;
      }

      throw new Error("검색 결과를 불러오지 못했습니다.");
    } catch (error) {
      setSearchStatus("error");
      setSearchError(error instanceof Error ? error.message : "검색 중 문제가 발생했습니다.");
    }
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSearch();
  };

  const handleNextRecommendation = () => {
    if (recommendationCandidates.length < 2) return;
    setRecommendationIndex((currentIndex) => {
      const randomIndex = Math.floor(Math.random() * (recommendationCandidates.length - 1));
      return randomIndex >= currentIndex ? randomIndex + 1 : randomIndex;
    });
  };

  return (
    <main className="hpoint-renewal hp-reservation-page hp-book-page">
      <Link
        className="hp-book-guide-entry"
        to="/mobilebookguide"
        aria-label="도서 대여·반납 이용 방법 보기"
      >
        <div>
          <span>처음 이용하시나요?</span>
          <strong>도서 대여·반납 이용 방법</strong>
        </div>
        <ChevronRightRoundedIcon aria-hidden="true" />
      </Link>
      <section className="hp-book-search" aria-label="도서 검색">
        <form role="search" onSubmit={handleSearchSubmit} noValidate>
          <SearchIcon className="hp-book-search__icon" aria-hidden="true" />
          <InputBase
            fullWidth
            value={searchText}
            placeholder="도서 제목을 검색하세요"
            inputProps={{
              "aria-label": "도서 제목 검색",
              "aria-describedby": searchInputError ? "book-search-error" : undefined,
            }}
            onChange={(event) => {
              setSearchText(event.target.value);
              if (searchInputError) setSearchInputError("");
            }}
            onCompositionStart={() => {
              isComposing.current = true;
            }}
            onCompositionEnd={() => {
              isComposing.current = false;
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              if (!isComposing.current) void handleSearch();
            }}
          />
          <Button className="hp-button hp-button--primary hp-book-search__button" type="submit" aria-label="도서 검색">
            검색
          </Button>
        </form>
        {searchInputError ? (
          <p id="book-search-error" className="hp-book-search__error" role="alert">
            {searchInputError}
          </p>
        ) : null}
      </section>

      <section className="hp-book-recommendation" aria-labelledby="book-recommendation-heading">
        <div className="hp-book-section-heading">
          <h2 id="book-recommendation-heading" className="hp-section-heading">
            추천도서
          </h2>
          <span className="hp-book-section-heading__date">{recommendationDate}</span>
        </div>

        {bookStatus === "loading" ? (
          <div className="hp-book-recommendation__skeleton" aria-label="추천 도서 불러오는 중" role="status">
            <Skeleton variant="rounded" width={112} height={160} />
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Skeleton width="35%" />
              <Skeleton width="80%" height={28} />
              <Skeleton width="50%" />
              <Skeleton width="100%" height={48} />
            </Stack>
          </div>
        ) : null}

        {bookStatus === "error" ? (
          <div className="hp-empty-state hp-book-empty-state">
            <MenuBookRoundedIcon aria-hidden="true" />
            <p>{bookError}</p>
            <Button className="hp-button hp-button--secondary" onClick={() => void loadBookHome()} startIcon={<RefreshRoundedIcon />}>
              다시 불러오기
            </Button>
          </div>
        ) : null}

        {bookStatus === "ready" && !recommendation ? (
          <div className="hp-empty-state hp-book-empty-state">
            <MenuBookRoundedIcon aria-hidden="true" />
            <p>소개할 신착 도서가 아직 없습니다.</p>
          </div>
        ) : null}

        {bookStatus === "ready" && recommendation ? (
          <article className="hp-book-recommendation__content">
            <div className="hp-book-recommendation__visual">
              <span className="hp-book-recommendation__label">오늘의 한 권</span>
              <BookCover book={recommendation} className="hp-book-cover hp-book-recommendation__cover" />
              <BorrowedBadge book={recommendation} />
              <span className="hp-book-recommendation__number" aria-hidden="true">
                {String(
                  (recommendationIndex % recommendationCandidates.length) + 1,
                ).padStart(2, "0")}
              </span>
            </div>
            <div className="hp-book-recommendation__detail">
              <div className="hp-book-meta-chips">
                {recommendation.tag ? <Chip className="hp-chip" label={recommendation.tag} size="small" /> : null}
                {recommendation.badge ? <Chip className="hp-chip" label={recommendation.badge} size="small" /> : null}
              </div>
              <h3 data-testid="book-recommendation-title">{recommendation.title}</h3>
              <p className="hp-book-recommendation__author">{recommendation.author || "작가 정보 없음"}</p>
              <p className="hp-book-recommendation__maincopy">
                {recommendation.maincopy || recommendation.description || "작은 도서관이 추천하는 도서입니다."}
              </p>
              {recommendation.maincopy && recommendation.description ? (
                <p className="hp-book-recommendation__description">{recommendation.description}</p>
              ) : null}
              <div className="hp-book-recommendation__actions">
                {getSafeExternalUrl(recommendation.url) ? (
                  <Button
                    className="hp-button hp-button--secondary"
                    component="a"
                    href={getSafeExternalUrl(recommendation.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNewRoundedIcon />}
                  >
                    도서 정보 보기
                  </Button>
                ) : null}
                {recommendationCandidates.length > 1 ? (
                  <Button className="hp-button hp-button--primary" onClick={handleNextRecommendation} startIcon={<RefreshRoundedIcon />}>
                    다른 추천 보기
                  </Button>
                ) : null}
              </div>
            </div>
          </article>
        ) : null}
      </section>

      <section className="hp-book-new" aria-labelledby="new-book-heading">
        <div className="hp-book-section-heading">
          <h2 id="new-book-heading" className="hp-section-heading">
            새로 들어온 책
          </h2>
          {bookStatus === "ready" && bookHome.newBooks.length ? <span className="hp-book-section-heading__count">{bookHome.newBooks.length}권</span> : null}
        </div>

        {bookStatus === "loading" ? (
          <div className="hp-book-new__skeleton" aria-hidden="true">
            {[0, 1, 2].map((item) => (
              <Skeleton key={item} variant="rounded" width={142} height={236} />
            ))}
          </div>
        ) : null}

        {bookStatus === "ready" && bookHome.newBooks.length ? (
          <div className="hp-book-new__list" role="list" aria-label="신착 도서 목록">
            {bookHome.newBooks.map((book, index) => {
              const safeUrl = getSafeExternalUrl(book.url);
              const content = (
                <>
                  <div className="hp-book-new__visual">
                    <BookCover book={book} className="hp-book-cover hp-book-new__cover" />
                    <BorrowedBadge book={book} />
                  </div>
                  <div className="hp-book-new__detail">
                    <div className="hp-book-meta-chips">
                      {book.tag ? <Chip className="hp-chip" label={book.tag} size="small" /> : null}
                      {book.badge ? <Chip className="hp-chip" label={book.badge} size="small" /> : null}
                    </div>
                    <h3 className="hp-book-new__title">{book.title}</h3>
                    <p>{book.author || "작가 정보 없음"}</p>
                  </div>
                </>
              );

              return safeUrl ? (
                <a key={`${book.title}-${index}`} className="hp-book-new__item" href={safeUrl} target="_blank" rel="noopener noreferrer" role="listitem">
                  {content}
                </a>
              ) : (
                <article key={`${book.title}-${index}`} className="hp-book-new__item" role="listitem">
                  {content}
                </article>
              );
            })}
          </div>
        ) : null}
      </section>

      {categoryIds.map((section) => {
        const books = bookHome.categories[section];
        const sectionId = `book-category-${section}`;

        return (
          <section key={section} className="hp-book-category" aria-labelledby={sectionId}>
            <button
              className="hp-book-category__heading"
              type="button"
              onClick={() => navigate(`/mobilebooktemplate/category/${section}`)}
              aria-label={`${categoryLabels[section]} 자세히 보기`}
            >
              <div>
                <span className="hp-book-category__eyebrow">작은 도서관</span>
                <h2 id={sectionId} className="hp-section-heading">
                  {categoryLabels[section]}
                </h2>
              </div>
              <ChevronRightRoundedIcon className="hp-book-category__arrow" aria-hidden="true" />
            </button>

            {bookStatus === "loading" ? (
              <div className="hp-book-category__list hp-book-category__skeleton" aria-hidden="true">
                {[0, 1, 2].map((item) => (
                  <Skeleton key={item} variant="rounded" />
                ))}
              </div>
            ) : null}

            {bookStatus === "ready" && books.length ? (
              <div className="hp-book-category__list" role="list" aria-label={`${categoryLabels[section]} 도서 목록`}>
                {books.map((book, index) => {
                  const safeUrl = getSafeExternalUrl(book.url);
                  const content = (
                    <>
                      <div className="hp-book-tile__visual">
                        <BookCover book={book} className="hp-book-cover hp-book-tile__cover" />
                        <BorrowedBadge book={book} />
                        {book.badge ? <span className="hp-book-tile__badge">{book.badge}</span> : null}
                      </div>
                      <div className="hp-book-tile__detail">
                        {book.tag ? <span className="hp-book-tile__tag">{book.tag}</span> : null}
                        <h3>{book.title}</h3>
                        <p>{book.author || "작가 정보 없음"}</p>
                      </div>
                    </>
                  );

                  return safeUrl ? (
                    <a
                      key={`${book.title}-${index}`}
                      className="hp-book-tile"
                      href={safeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="listitem"
                    >
                      {content}
                    </a>
                  ) : (
                    <article key={`${book.title}-${index}`} className="hp-book-tile" role="listitem">
                      {content}
                    </article>
                  );
                })}
              </div>
            ) : null}

            {bookStatus === "ready" && !books.length ? (
              <p className="hp-book-category__empty">{categoryLabels[section]} 도서를 준비하고 있습니다.</p>
            ) : null}
          </section>
        );
      })}

      <SearchBookModal
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        results={searchResults}
        searchText={searchText.trim()}
        loading={searchStatus === "loading"}
        error={searchStatus === "error" ? searchError : undefined}
        onRetry={() => void handleSearch()}
      />
    </main>
  );
};

export default MobileBookTemplate;
